import { useQueryClient } from '@tanstack/react-query';
import { useFetchReviewQuery } from './queries/useFetchReviewQuery';
import { useParams } from 'react-router-dom';
import { useFetchProductQuery } from '../../../../../hooks/product/useFetchProductQuery';
import { convertProductDataForLegacyComponent } from '../util/util';
import { useAccountUserDetailQueryV2 } from '../../../../account/user/detail/common/hooks';
import useUpdateReviewQuery, { ReviewUpdateRequestPayloadType } from './queries/useUpdateReviewQuery';
import { useFetchPointLogsQuery } from '../../../../../hooks/queries/point/useFetchPointLogsQuery';
import usePointRetrieveQuery from './queries/usePointRetrieveQuery';
import { validateError } from '../../../../../lib/utils/validateError';
import { ReviewFindOneResponseBodyReviewDto, ReviewStatus } from '../types/ReviewDetailTypes';
import toastMsg from '../../../../../lib/toastMsg';
import { REVIEW } from '../../../../../constants/queryKeys';
import { PointLogsFindResponseBodyDto } from '../../../../../types/api/point-logs';
import { ProductInfoType } from '../../../../../types/product';
import { AccountUserDetailTypeV2 } from '../../../../account/user/detail/common/types/accountUserDetail';
import { useReviewCriteriaQuery } from './queries/useReviewCriteriaQuery';
import { ReviewCriteriaFindResponseDto } from '../types/ReviewCriteriaTypes';

type UseReviewEditReturnType = {
  reviewDetailInfo?: ReviewFindOneResponseBodyReviewDto;
  userDetailInfo?: AccountUserDetailTypeV2;
  productInfo?: ProductInfoType;
  pointLogsInfo?: PointLogsFindResponseBodyDto;
  criteriaInfo?: ReviewCriteriaFindResponseDto;
  isLoading: boolean;
  isSuccess: boolean;
  handleClickRetrievePoint: (status: string, memo: string) => void;
  canPointRetrieved: boolean;
  productId?: number;
  updateReview?: (payload: ReviewUpdateRequestPayloadType) => void;
};

type reviewIdParams = {
  id: string;
};

const useReviewEdit = (): UseReviewEditReturnType => {
  const { id } = useParams<reviewIdParams>();
  const queryClient = useQueryClient();

  const {
    data: reviewDetailInfo,
    isLoading,
    isSuccess,
  } = useFetchReviewQuery({
    id: Number(id),
    options: {
      enabled: !!id,
      select: (data) => data.review,
    },
  });

  const productId = reviewDetailInfo?.productId;
  const userId = reviewDetailInfo?.user.id;

  const { data: productInfo } = useFetchProductQuery({
    productId,
    options: {
      select: convertProductDataForLegacyComponent,
    },
  });

  const categoryId = productInfo?.categoryId;

  const { data: criteriaInfo } = useReviewCriteriaQuery<ReviewCriteriaFindResponseDto>({
    id: categoryId,
  });

  const { data: userDetailInfo } = useAccountUserDetailQueryV2({
    userId: userId,
    options: {
      enabled: !!userId,
      select: (result) => result.user,
    },
  });

  const { data: pointLogsInfo } = useFetchPointLogsQuery<PointLogsFindResponseBodyDto>({
    params: {
      userId,
    },
  });

  const { mutate: updateReview } = useUpdateReviewQuery();

  const { mutate } = usePointRetrieveQuery();
  const handleClickRetrievePoint = (status: string, memo: string) => {
    validateError(
      [
        {
          if: status !== ReviewStatus['DELETED_FROM_ADMIN'],
          onError: () => toastMsg.error('노출 여부가 비 활성이 아닙니다.'),
        },
        {
          if: reviewDetailInfo && reviewDetailInfo?.receivedPointAmount <= 0,
          onError: () => toastMsg.error('회수가능한 포인트가 존재하지 않습니다.'),
        },
      ],
      () => {
        mutate(
          {
            id: reviewDetailInfo?.id,
            data: {
              memo,
            },
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries([...REVIEW.DETAIL, reviewDetailInfo?.id]);
            },
          },
        );
      },
    );
  };

  const canPointRetrieved = !!reviewDetailInfo?.receivedPointAmount && !reviewDetailInfo?.pointReturned;

  return {
    reviewDetailInfo,
    productInfo,
    userDetailInfo,
    pointLogsInfo,
    criteriaInfo,
    isLoading,
    isSuccess,
    handleClickRetrievePoint,
    canPointRetrieved,
    productId,
    updateReview,
  };
};

export default useReviewEdit;
